import { dayjsUtils } from '@shared/utils/helpers/dayjs';

import type { DateRangeOption } from '@shared/features/views/components/entities-filters/type';

export function buildDateRanges({
  afterKey,
  beforeKey,
  includeOnly,
}: {
  afterKey: string;
  beforeKey: string;
  includeOnly?: DateRangeOption[];
}) {
  const t = dayjsUtils();

  const today = () => ({
    [afterKey]: t.startOf('day').format('YYYY-MM-DD'),
    [beforeKey]: t.endOf('day').format('YYYY-MM-DD'),
  });

  const yesterday = () => ({
    [afterKey]: t.subtract(1, 'day').startOf('day').format('YYYY-MM-DD'),
    [beforeKey]: t.subtract(1, 'day').endOf('day').format('YYYY-MM-DD'),
  });

  const last7Days = () => ({
    [afterKey]: t.subtract(6, 'day').startOf('day').format('YYYY-MM-DD'),
    [beforeKey]: t.endOf('day').format('YYYY-MM-DD'),
  });

  const nextWeek = () => ({
    [afterKey]: t.startOf('day').format('YYYY-MM-DD'),
    [beforeKey]: t.add(7, 'day').endOf('day').format('YYYY-MM-DD'),
  });

  const lastMonthStart = t.subtract(1, 'month');
  const lastMonth = () => ({
    [afterKey]: lastMonthStart.format('YYYY-MM-DD'),
    [beforeKey]: t.format('YYYY-MM-DD'),
  });

  const exactDate = (date?: string) => ({
    [afterKey]: dayjsUtils(date).startOf('day').format('YYYY-MM-DD'),
    [beforeKey]: dayjsUtils(date).endOf('day').format('YYYY-MM-DD'),
  });

  const beforeDate = (date?: string) => ({
    [beforeKey]: dayjsUtils(date).endOf('day').format('YYYY-MM-DD'),
  });

  const afterDate = (date?: string) => ({
    [afterKey]: dayjsUtils(date).startOf('day').format('YYYY-MM-DD'),
  });

  const dateRange = (startDate?: string, endDate?: string) => ({
    [afterKey]: dayjsUtils(startDate).format('YYYY-MM-DD'),
    [beforeKey]: dayjsUtils(endDate).format('YYYY-MM-DD'),
  });

  const allRanges = [
    {
      label: 'Today',
      friendlyLabel() {
        return 'Today';
      },
      createDate: today,
      type: 'option',
    },
    {
      label: 'Yesterday',
      friendlyLabel() {
        return 'Yesterday';
      },
      createDate: yesterday,
      type: 'option',
    },
    {
      label: 'Last 7 Days',
      friendlyLabel() {
        return 'Last 7 Days';
      },
      createDate: last7Days,
      type: 'option',
    },
    {
      label: 'Next Week',
      friendlyLabel() {
        return 'Next Week';
      },
      createDate: nextWeek,
      type: 'option',
    },
    {
      label: 'Last Month',
      friendlyLabel() {
        return 'Last Month';
      },
      createDate: lastMonth,
      type: 'option',
    },
    {
      label: 'Exact Date',
      friendlyLabel({ date }: { date?: string }) {
        return `Exact Date: ${date}`;
      },
      createDate: exactDate,
      type: 'date',
      isSingleDate: true,
    },
    {
      label: 'Before Date',
      friendlyLabel({ date }: { date?: string }) {
        return `Before Date: ${date}`;
      },
      createDate: beforeDate,
      type: 'date',
      isSingleDate: true,
    },
    {
      label: 'After Date',
      friendlyLabel({ date }: { date?: string }) {
        return `After Date: ${date}`;
      },
      createDate: afterDate,
      type: 'date',
      isSingleDate: true,
    },
    {
      label: 'Date Range',
      friendlyLabel({ before, after }: { before?: string; after?: string }) {
        return `Date Range: ${before} - ${after}`;
      },
      createDate: dateRange,
      type: 'date',
      isSingleDate: false,
    },
  ];

  if (includeOnly?.length) {
    return allRanges.filter((range) =>
      includeOnly.includes(range.label as DateRangeOption)
    );
  }

  return allRanges;
}
